import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProjects } from '../context/ProjectsContext';
import { useSession } from '../context/SessionContext';
import { useUserProfile } from '../context/UserProfileContext';
import { invokeFunction } from '../lib/functionsClient';
import { supabase } from '../lib/supabaseClient';
import ProjectKeys from '../components/ProjectKeys';
import CodeSnippet from '../components/CodeSnippet';
import type { Collection, Item, Project } from '../types/api';

const stepLabels = ['Projects', 'Collections', 'Items', 'Project Settings', 'Collection Settings', 'Account'] as const;
const accessFields = ['allow_anonymous_read', 'allow_anonymous_append', 'allow_anonymous_delete'] as const;
type Step = 0 | 1 | 2 | 3 | 4 | 5;
type SearchStep = 0 | 1 | 2;
type SortDirection = 'asc' | 'desc';
type ItemSortColumn = 'item' | 'value' | 'updated' | 'created';

type ToolbarMenuButtonProps = {
  children?: React.ReactNode;
  className?: string;
  'aria-label'?: string;
};

type ToolbarMenuButtonElement = React.ReactElement<ToolbarMenuButtonProps>;

const isNodeWithChildren = (
  node: React.ReactNode,
): node is React.ReactElement<{ children?: React.ReactNode }> =>
  React.isValidElement<{ children?: React.ReactNode }>(node);

const isToolbarMenuButtonElement = (node: React.ReactNode): node is ToolbarMenuButtonElement =>
  React.isValidElement<ToolbarMenuButtonProps>(node);

const ITEM_SORT_LABELS: Record<ItemSortColumn, string> = {
  item: 'Item',
  value: 'Value',
  updated: 'Updated',
  created: 'Created',
};

type DialogState =
  | null
  | { type: 'create-project' }
  | { type: 'create-collection' }
  | { type: 'add-item' }
  | { type: 'edit-item'; item: Item };

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? 'https://your-project.supabase.co';

const buildItemSnippet = (project: Project, collection?: Collection | null) => {
  const collectionId = collection?.id ?? '<collection-id>';
  return `curl -X POST "${supabaseUrl}/functions/v1/item-create" \
  -H "Authorization: Bearer $\{SUPABASE_JWT}" \
  -H "x-shanework-key: ${project.active_key}" \
  -H "Content-Type: application/json" \
  -d '{
        "collectionId": "${collectionId}",
        "key": "kv-key",
        "value": { "foo": "bar" }
      }'`;
};

const buildListSnippet = (project: Project, collection?: Collection | null) => {
  const collectionId = collection?.id ?? '<collection-id>';
  return `const res = await fetch('${supabaseUrl}/functions/v1/item-list', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Authorization: 'Bearer YOUR_SUPABASE_JWT',
    'x-shanework-key': '${project.active_key}',
  },
  body: JSON.stringify({ collectionId: '${collectionId}' }),
});
const { items } = await res.json();`;
};

const parseJson = (value: string): { ok: true; data: unknown } | { ok: false; message: string } => {
  try {
    return { ok: true, data: JSON.parse(value) };
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : 'Invalid JSON' };
  }
};

const formatTimestamp = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—');

const createVerificationCode = () => Math.floor(1000 + Math.random() * 9000).toString();

const MenuLoadingIndicator = ({ active }: { active: boolean }) =>
  active ? (
    <div className="menu-loading-indicator" role="status" aria-live="polite">
      <span className="menu-loading-spinner" aria-hidden="true" />
      <span className="sr-only">Loading data…</span>
    </div>
  ) : null;

const ROLE_LABELS: Record<'admin' | 'user' | 'pending', string> = {
  admin: 'Admin',
  user: 'User',
  pending: 'Pending',
};

const MOBILE_MENU_QUERY = '(max-width: 720px)';
const COMPACT_MENU_QUERY = '(max-width: 520px)';

const useMediaQuery = (query: string): boolean => {
  const getMatches = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    setMatches(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => {
        mediaQueryList.removeEventListener('change', handleChange);
      };
    }

    mediaQueryList.addListener(handleChange);
    return () => {
      mediaQueryList.removeListener(handleChange);
    };
  }, [query]);

  return matches;
};

const WorkspacePage = () => {
  const session = useSession();
  const navigate = useNavigate();
  const { profile } = useUserProfile();
  const isAdmin = profile?.role === 'admin';
  const userRoleLabel = profile ? ROLE_LABELS[profile.role] : '—';
  const { projects, loading: projectsLoading, error: projectsError, refresh, setProject } = useProjects();

  const [currentStep, setCurrentStep] = useState<Step>(0);
  const [searchState, setSearchState] = useState<[string, string, string]>(['', '', '']);
  const [searchExpanded, setSearchExpanded] = useState<[boolean, boolean, boolean]>([false, false, false]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState<string | null>(null);
  const [projectsActionError, setProjectsActionError] = useState<string | null>(null);
  const [collectionsActionError, setCollectionsActionError] = useState<string | null>(null);
  const [collectionsBootstrapped, setCollectionsBootstrapped] = useState(false);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [itemsBootstrapped, setItemsBootstrapped] = useState(false);
  const [collectionMeta, setCollectionMeta] = useState<Collection | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState({
    projectName: '',
    collectionName: '',
    itemKey: '',
    itemValue: '{"name":"example"}',
  });
  const [deleteDialog, setDeleteDialog] = useState<{ project: Project; code: string } | null>(null);
  const [deleteInputs, setDeleteInputs] = useState({ name: '', code: '' });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rotateMessage, setRotateMessage] = useState<string | null>(null);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [selectedCollections, setSelectedCollections] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [projectSortOrder, setProjectSortOrder] = useState<SortDirection>('asc');
  const [collectionSortOrder, setCollectionSortOrder] = useState<SortDirection>('asc');
  const [itemSort, setItemSort] = useState<{ column: ItemSortColumn; direction: SortDirection }>({
    column: 'updated',
    direction: 'desc',
  });
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const shimmerAnimationFrame = useRef<number | null>(null);
  const isMobileMenu = useMediaQuery(MOBILE_MENU_QUERY);
  const isCompactMobileMenu = useMediaQuery(COMPACT_MENU_QUERY);
  const [mobileToolbarMenuStep, setMobileToolbarMenuStep] = useState<SearchStep | null>(null);
  const toolbarMenuRefs = useRef<Record<SearchStep, HTMLDivElement | null>>({
    0: null,
    1: null,
    2: null,
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    if (!root) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (shimmerAnimationFrame.current) {
        cancelAnimationFrame(shimmerAnimationFrame.current);
      }
      shimmerAnimationFrame.current = requestAnimationFrame(() => {
        const width = window.innerWidth || 1;
        const height = window.innerHeight || 1;
        const x = Math.min(Math.max(event.clientX / width, 0), 1) * 100;
        const y = Math.min(Math.max(event.clientY / height, 0), 1) * 100;
        root.style.setProperty('--bg-shimmer-x', `${x}%`);
        root.style.setProperty('--bg-shimmer-y', `${y}%`);
        root.style.setProperty('--bg-shimmer-opacity', '0.65');
      });
    };

    const handlePointerLeave = () => {
      if (shimmerAnimationFrame.current) {
        cancelAnimationFrame(shimmerAnimationFrame.current);
        shimmerAnimationFrame.current = null;
      }
      root.style.setProperty('--bg-shimmer-opacity', '0');
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerleave', handlePointerLeave);
    window.addEventListener('blur', handlePointerLeave);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', handlePointerLeave);
      window.removeEventListener('blur', handlePointerLeave);
      handlePointerLeave();
    };
  }, []);

  useEffect(() => {
    if (mobileToolbarMenuStep === null || typeof document === 'undefined') return;
    const handlePointerDown = (event: PointerEvent) => {
      const activeRef = toolbarMenuRefs.current[mobileToolbarMenuStep];
      if (activeRef && event.target instanceof Node && activeRef.contains(event.target)) {
        return;
      }
      setMobileToolbarMenuStep(null);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [mobileToolbarMenuStep]);

  useEffect(() => {
    if (!isCompactMobileMenu && mobileToolbarMenuStep !== null) {
      setMobileToolbarMenuStep(null);
    }
  }, [isCompactMobileMenu, mobileToolbarMenuStep]);

  const activeProject = useMemo(() => projects.find((project) => project.id === activeProjectId) ?? null, [projects, activeProjectId]);
  const activeCollection = useMemo(
    () => collections.find((collection) => collection.id === activeCollectionId) ?? null,
    [collections, activeCollectionId],
  );
  const userInitials = useMemo(() => {
    const source = session.user?.email ?? session.user?.id ?? 'U';
    const tokens = source.split(/[^A-Za-z0-9]/).filter(Boolean);
    const letters = (tokens.length ? tokens : [source]).map((token) => token.charAt(0)).join('');
    return letters.slice(0, 2).toUpperCase();
  }, [session.user]);
  const accountEmail = session.user?.email ?? '—';
  const accountId = session.user?.id ?? '—';
  const accountCreated = session.user?.created_at ? new Date(session.user.created_at).toLocaleDateString() : '—';
  const menuLoading = projectsLoading || collectionsLoading || itemsLoading;

  const setStep = (nextStep: Step, options: { force?: boolean } = {}) => {
    const { force = false } = options;
    const maxStep: Step = 5;
    const safeStep = Math.max(0, Math.min(nextStep, maxStep)) as Step;
    if (!force) {
      if (safeStep === 1 && !activeProject) return;
      if (safeStep === 2 && !activeCollection) return;
      if (safeStep === 3 && !activeProject) return;
      if (safeStep === 4 && (!activeProject || !activeCollection)) return;
      if (safeStep === 5 && !session?.user) return;
    }
    setCurrentStep(safeStep);
  };

  const loadCollections = useCallback(
    async (project: Project, silent = false) => {
      if (!project) return;
      if (!silent) {
        setCollectionsLoading(true);
      }
      setCollectionsBootstrapped(false);
      setCollectionsError(null);
      const { data, error } = await invokeFunction<{ collections: Collection[] }>('create-collection', {
        body: { action: 'list', projectId: project.id },
        bearer: project.active_key,
      });
      if (error) {
        setCollectionsError(error);
        setCollectionsBootstrapped(true);
        setCollectionsLoading(false);
        return;
      }
      setCollections(data?.collections ?? []);
      setCollectionsBootstrapped(true);
      setCollectionsLoading(false);
    },
    [],
  );

  const loadItems = useCallback(
    async (project: Project, collectionId: string, opts: { query?: string } = {}) => {
      setItemsLoading(true);
      setItemsBootstrapped(false);
      setItemsError(null);
      const fnName = opts.query ? 'item-search' : 'item-list';
      const body = opts.query ? { collectionId, query: opts.query } : { collectionId };
      const { data, error } = await invokeFunction<{ items: Item[]; collection?: Collection }>(fnName, {
        body,
        bearer: project.active_key,
      });
      if (error) {
        setItemsError(error);
        setItemsBootstrapped(true);
        setItemsLoading(false);
        return;
      }
      setItems(data?.items ?? []);
      if (data?.collection) {
        setCollectionMeta(data.collection);
      } else if (activeCollection) {
        setCollectionMeta(activeCollection);
      }
      setItemsBootstrapped(true);
      setItemsLoading(false);
    },
    [activeCollection],
  );

  useEffect(() => {
    if (!activeProjectId) {
      setCollections([]);
      setActiveCollectionId(null);
      setCollectionsBootstrapped(false);
      return;
    }
    const project = projects.find((p) => p.id === activeProjectId);
    if (!project) {
      setActiveProjectId(null);
      setCollections([]);
      setActiveCollectionId(null);
      return;
    }
    void loadCollections(project);
  }, [activeProjectId, projects, loadCollections]);

  useEffect(() => {
    if (!activeProject || !activeCollectionId) {
      setItems([]);
      setCollectionMeta(null);
      setSelectedItems(new Set());
      if (!activeCollectionId) {
        setItemsBootstrapped(false);
      }
      return;
    }
    void loadItems(activeProject, activeCollectionId);
  }, [activeProject, activeCollectionId, loadItems]);

  useEffect(() => {
    setSelectedProjects((prev) => {
      const next = new Set<string>();
      projects.forEach((project) => {
        if (prev.has(project.id)) {
          next.add(project.id);
        }
      });
      return next;
    });
  }, [projects]);

  useEffect(() => {
    setSelectedCollections((prev) => {
      const next = new Set<string>();
      collections.forEach((collection) => {
        if (prev.has(collection.id)) {
          next.add(collection.id);
        }
      });
      return next;
    });
  }, [collections]);

  useEffect(() => {
    if (copyToast) {
      const timeout = setTimeout(() => setCopyToast(null), 2500);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [copyToast]);

  useEffect(() => {
    if (currentStep !== 0 && selectedProjects.size) {
      setSelectedProjects(new Set());
    }
    if (currentStep !== 1 && selectedCollections.size) {
      setSelectedCollections(new Set());
    }
    if (currentStep !== 2 && selectedItems.size) {
      setSelectedItems(new Set());
    }
  }, [currentStep, selectedProjects.size, selectedCollections.size, selectedItems.size]);

  const handleProjectSelection = (projectId: string) => {
    setActiveProjectId(projectId);
    setActiveCollectionId(null);
    setCollectionMeta(null);
    setSelectedItems(new Set());
    setSearchState(['', '', ''] as [string, string, string]);
    setSearchExpanded([false, false, false] as [boolean, boolean, boolean]);
    setStep(1, { force: true });
  };

  const handleCollectionSelection = (collectionId: string) => {
    setActiveCollectionId(collectionId);
    setSelectedItems(new Set());
    setSearchState((prev) => {
      const next = [...prev] as [string, string, string];
      next[2] = '';
      return next;
    });
    setSearchExpanded((prev) => {
      const next = [...prev] as [boolean, boolean, boolean];
      next[2] = false;
      return next;
    });
    setStep(2, { force: true });
  };

  const handleLogout = useCallback(() => {
    setLogoutLoading(true);
    void supabase.auth
      .signOut()
      .then(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigate('/login', { replace: true });
      })
      .catch(() => {
        setLogoutLoading(false);
      });
  }, [navigate]);

  const clearStateBeyondStep = (targetStep: Step) => {
    if (targetStep < 2) {
      setActiveCollectionId(null);
      setCollectionMeta(null);
      setItems([]);
      setItemsError(null);
      setItemsBootstrapped(false);
      setItemsLoading(false);
      setSelectedItems(new Set());
    }
    if (targetStep < 1) {
      setActiveProjectId(null);
      setCollections([]);
      setCollectionsError(null);
      setCollectionsActionError(null);
      setCollectionsBootstrapped(false);
      setCollectionsLoading(false);
      setActiveCollectionId(null);
      setSelectedCollections(new Set());
    }
    const searchStepsToReset: SearchStep[] = targetStep < 1 ? [1, 2] : targetStep < 2 ? [2] : [];
    if (searchStepsToReset.length) {
      resetSearchStateForSteps(searchStepsToReset);
    }
  };

  const handleBreadcrumbClick = (targetStep: Step) => {
    if (targetStep === currentStep) return;
    if (targetStep < currentStep) {
      clearStateBeyondStep(targetStep);
      setStep(targetStep);
      return;
    }
    if (targetStep === currentStep + 1) {
      setStep(targetStep);
    }
  };

  const handleTitleClick = () => {
    setStep(0, { force: true });
  };

  const breadcrumbTrail = useMemo(() => {
    if (currentStep === 3) {
      return [
        { label: 'Projects', step: 0 as Step },
        { label: 'Project Settings', step: 3 as Step },
      ];
    }
    if (currentStep === 4) {
      return [
        { label: 'Projects', step: 0 as Step },
        { label: 'Collections', step: 1 as Step },
        { label: 'Collection Settings', step: 4 as Step },
      ];
    }
    if (currentStep === 5) {
      return [
        { label: 'Projects', step: 0 as Step },
        { label: 'Account Settings', step: 5 as Step },
      ];
    }
    return stepLabels.slice(0, currentStep + 1).map((label, index) => ({
      label,
      step: index as Step,
    }));
  }, [currentStep]);

  const getSearchStep = (): SearchStep | null => {
    return currentStep < 3 ? (currentStep as SearchStep) : null;
  };

  useEffect(() => {
    if (mobileToolbarMenuStep === null) return;
    const activeSearchStep = getSearchStep();
    if (activeSearchStep === null || activeSearchStep !== mobileToolbarMenuStep) {
      setMobileToolbarMenuStep(null);
    }
  }, [currentStep, mobileToolbarMenuStep]);

  const setSearchExpandedForStep = (step: SearchStep, value: boolean) => {
    setSearchExpanded((prev) => {
      if (prev[step] === value) return prev;
      const next = [...prev] as [boolean, boolean, boolean];
      next[step] = value;
      return next;
    });
  };

  const setSearchValueForStep = (step: SearchStep, value: string) => {
    setSearchState((prev) => {
      if (prev[step] === value) return prev;
      const next = [...prev] as [string, string, string];
      next[step] = value;
      return next;
    });
  };

  const expandSearch = () => {
    const step = getSearchStep();
    if (step === null) return;
    setSearchExpandedForStep(step, true);
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  };

  const collapseSearchIfEmpty = (step: SearchStep) => {
    if (searchState[step]?.trim()) return;
    setSearchExpandedForStep(step, false);
  };

  const resetSearchStateForSteps = (stepsToReset: SearchStep[]) => {
    if (!stepsToReset.length) return;
    setSearchState((prev) => {
      let next: [string, string, string] | null = null;
      stepsToReset.forEach((step) => {
        if (prev[step]) {
          if (!next) {
            next = [...prev] as [string, string, string];
          }
          next[step] = '';
        }
      });
      return next ?? prev;
    });
    setSearchExpanded((prev) => {
      let next: [boolean, boolean, boolean] | null = null;
      stepsToReset.forEach((step) => {
        if (prev[step]) {
          if (!next) {
            next = [...prev] as [boolean, boolean, boolean];
          }
          next[step] = false;
        }
      });
      return next ?? prev;
    });
  };

  const handleClearSearch = () => {
    const step = getSearchStep();
    if (step === null) return;
    const hadValue = Boolean(searchState[step]);
    setSearchValueForStep(step, '');
    if (step === 2 && hadValue) {
      void runItemSearch('');
    }
    setSearchExpandedForStep(step, false);
    searchInputRef.current?.blur();
  };

  const resolveItemUpdatedTimestamp = (entry: Item): string | null => {
    const record = entry as Item & { updated_at?: unknown };
    if (typeof record.updated_at === 'string') {
      return record.updated_at;
    }
    const createdAt: unknown = entry.created_at;
    return typeof createdAt === 'string' ? createdAt : null;
  };

  const resolveItemCreatedTimestamp = (entry: Item): string | null => {
    const createdAt: unknown = entry.created_at;
    return typeof createdAt === 'string' ? createdAt : null;
  };

  const resolveProjectForItem = (item: Item): Project | null => {
    if (activeProject?.id === item.project_id) {
      return activeProject;
    }
    return projects.find((project) => project.id === item.project_id) ?? null;
  };

  const getNumericTimestamp = (value: string | null): number => {
    if (!value) return 0;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : 0;
  };

  const filteredProjects = useMemo(() => {
    const term = searchState[0]?.trim().toLowerCase() ?? '';
    const pool = term ? projects.filter((project) => project.name.toLowerCase().includes(term)) : projects;
    const sorted = [...pool].sort((a, b) => {
      const archivedA = Boolean(a.archived_at);
      const archivedB = Boolean(b.archived_at);
      if (archivedA !== archivedB) {
        return archivedA ? 1 : -1;
      }
      return projectSortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    });
    return sorted;
  }, [projects, searchState, projectSortOrder]);

  const filteredCollections = useMemo(() => {
    if (!activeProject) return [];
    const term = searchState[1]?.trim().toLowerCase() ?? '';
    const pool = term ? collections.filter((collection) => collection.name.toLowerCase().includes(term)) : collections;
    const sorted = [...pool].sort((a, b) =>
      collectionSortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name),
    );
    return sorted;
  }, [collections, activeProject, searchState, collectionSortOrder]);

  const filteredItems = useMemo(() => {
    const term = searchState[2]?.trim().toLowerCase() ?? '';
    const pool = term
      ? items.filter((item) => item.key.toLowerCase().includes(term) || JSON.stringify(item.value).toLowerCase().includes(term))
      : items;
    const sorted = [...pool].sort((a, b) => {
      const { column, direction } = itemSort;
      const multiplier = direction === 'asc' ? 1 : -1;
      if (column === 'item') {
        return multiplier * a.key.localeCompare(b.key, undefined, { sensitivity: 'base' });
      }
      if (column === 'value') {
        const aValue = JSON.stringify(a.value);
        const bValue = JSON.stringify(b.value);
        return multiplier * aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
      }
      const diff =
        column === 'created'
          ? getNumericTimestamp(resolveItemCreatedTimestamp(a)) - getNumericTimestamp(resolveItemCreatedTimestamp(b))
          : getNumericTimestamp(resolveItemUpdatedTimestamp(a)) - getNumericTimestamp(resolveItemUpdatedTimestamp(b));
      return multiplier * (diff === 0 ? 0 : diff > 0 ? 1 : -1);
    });
    return sorted;
  }, [items, searchState, itemSort]);

  const showCollectionsEmpty = collectionsBootstrapped && !collectionsLoading && !filteredCollections.length;
  const showItemsEmpty = itemsBootstrapped && !itemsLoading && !filteredItems.length;

  const toggleProjectSelection = (projectId: string, checked: boolean) => {
    setSelectedProjects((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(projectId);
      } else {
        next.delete(projectId);
      }
      return next;
    });
  };

  const toggleCollectionSelection = (collectionId: string, checked: boolean) => {
    setSelectedCollections((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(collectionId);
      } else {
        next.delete(collectionId);
      }
      return next;
    });
  };

  const clearProjectSelection = () => setSelectedProjects(new Set());
  const clearCollectionSelection = () => setSelectedCollections(new Set());
  const clearItemSelection = () => setSelectedItems(new Set());

  const toggleProjectSortOrder = () => {
    setProjectSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const toggleCollectionSortOrder = () => {
    setCollectionSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const toggleItemSort = (column: ItemSortColumn) => {
    setItemSort((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const getItemSortIcon = (column: ItemSortColumn) => {
    if (itemSort.column !== column) return 'unfold_more';
    return itemSort.direction === 'asc' ? 'north' : 'south';
  };

  const getItemSortAriaLabel = (column: ItemSortColumn) => {
    const direction =
      itemSort.column === column ? (itemSort.direction === 'asc' ? 'ascending' : 'descending') : 'ascending';
    return `Sort by ${ITEM_SORT_LABELS[column]} (${direction})`;
  };

  const toggleItemSelection = (itemId: string, checked: boolean) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return next;
    });
  };

  const resetDialogState = () => {
    setDialog(null);
    setFormLoading(false);
    setFormError(null);
    setFormValues((prev) => ({ ...prev, itemKey: '', itemValue: '{"name":"example"}' }));
  };

  const openEditItemDialog = (item: Item) => {
    setDialog({ type: 'edit-item', item });
    setFormValues((prev) => ({
      ...prev,
      itemValue: JSON.stringify(item.value, null, 2),
      itemKey: item.key,
    }));
  };

  const handleDialogDeleteItem = async (item: Item) => {
    const project = resolveProjectForItem(item);
    if (!project) {
      setFormError('Select a project first');
      return;
    }
    const confirmed = typeof window === 'undefined' ? true : window.confirm(`Delete ${item.key}?`);
    if (!confirmed) return;
    setFormLoading(true);
    setFormError(null);
    const { error } = await invokeFunction('item-delete', {
      body: { itemId: item.id },
      bearer: project.active_key,
    });
    setFormLoading(false);
    if (error) {
      setFormError(error);
      return;
    }
    setItems((prev) => prev.filter((entry) => entry.id !== item.id));
    setSelectedItems((prev) => {
      const next = new Set(prev);
      next.delete(item.id);
      return next;
    });
    resetDialogState();
  };

  const handleDialogDuplicateItem = async (item: Item) => {
    const project = resolveProjectForItem(item);
    if (!project) {
      setFormError('Select a project first');
      return;
    }
    const collectionId = item.collection_id ?? activeCollectionId;
    if (!collectionId) {
      setFormError('Select a collection first');
      return;
    }
    const suggested = `${item.key}-copy`;
    const input = typeof window === 'undefined' ? suggested : window.prompt('Key for duplicate item', suggested);
    const key = input?.trim();
    if (!key) return;
    let valuePayload: unknown = item.value;
    if (dialog?.type === 'edit-item' && dialog.item.id === item.id) {
      const parsed = parseJson(formValues.itemValue);
      if (!parsed.ok) {
        setFormError(parsed.message);
        return;
      }
      valuePayload = parsed.data;
    }
    setFormLoading(true);
    setFormError(null);
    const { data, error } = await invokeFunction<{ item: Item }>('item-create', {
      body: { collectionId, key, value: valuePayload },
      bearer: project.active_key,
    });
    setFormLoading(false);
    if (error) {
      setFormError(error);
      return;
    }
    if (data?.item) {
      setItems((prev) => [data.item, ...prev]);
    }
  };

  const handleDialogRenameItem = async (item: Item) => {
    const project = resolveProjectForItem(item);
    if (!project) {
      setFormError('Select a project first');
      return;
    }
    const input = typeof window === 'undefined' ? item.key : window.prompt('New key for this item', item.key);
    const key = input?.trim();
    if (!key || key === item.key) return;
    setFormLoading(true);
    setFormError(null);
    const { data, error } = await invokeFunction<{ item: Item }>('item-update', {
      body: { itemId: item.id, key },
      bearer: project.active_key,
    });
    setFormLoading(false);
    if (error) {
      setFormError(error);
      return;
    }
    if (data?.item) {
      setItems((prev) => prev.map((entry) => (entry.id === item.id ? data.item : entry)));
      setDialog({ type: 'edit-item', item: data.item });
      setFormValues((prev) => ({ ...prev, itemKey: key }));
    }
  };

  const submitCreateProject = async () => {
    if (!formValues.projectName.trim()) {
      setFormError('Project name is required');
      return;
    }
    setFormLoading(true);
    setFormError(null);
    const { data, error } = await invokeFunction<{ project: Project }>('create-project', {
      body: { action: 'create', name: formValues.projectName.trim(), createdBy: session.user.id },
    });
    setFormLoading(false);
    if (error) {
      setFormError(error);
      return;
    }
    if (data?.project) {
      setProject(data.project);
      setActiveProjectId(data.project.id);
      setFormValues((prev) => ({ ...prev, projectName: '' }));
      resetDialogState();
      setStep(1);
    }
  };

  const submitCreateCollection = async () => {
    if (!activeProject) {
      setFormError('Select a project first');
      return;
    }
    if (!formValues.collectionName.trim()) {
      setFormError('Collection name is required');
      return;
    }
    setFormLoading(true);
    setFormError(null);
    const { data, error } = await invokeFunction<{ collection: Collection }>('create-collection', {
      body: { action: 'create', projectId: activeProject.id, name: formValues.collectionName.trim() },
      bearer: activeProject.active_key,
    });
    setFormLoading(false);
    if (error) {
      setFormError(error);
      return;
    }
    if (data?.collection) {
      setCollections((prev) => [data.collection, ...prev]);
      setActiveCollectionId(data.collection.id);
      setFormValues((prev) => ({ ...prev, collectionName: '' }));
      resetDialogState();
      setStep(2);
    }
  };

  const submitAddItem = async () => {
    if (!activeProject || !activeCollectionId) {
      setFormError('Select a collection first');
      return;
    }
    if (!formValues.itemKey.trim()) {
      setFormError('Item key is required');
      return;
    }
    const parsed = parseJson(formValues.itemValue);
    if (!parsed.ok) {
      setFormError(parsed.message);
      return;
    }
    setFormLoading(true);
    setFormError(null);
    const { data, error } = await invokeFunction<{ item: Item }>('item-create', {
      body: { collectionId: activeCollectionId, key: formValues.itemKey.trim(), value: parsed.data },
      bearer: activeProject.active_key,
    });
    setFormLoading(false);
    if (error) {
      setFormError(error);
      return;
    }
    if (data?.item) {
      setItems((prev) => [data.item, ...prev]);
      setFormValues((prev) => ({ ...prev, itemKey: '', itemValue: '{"foo":"bar"}' }));
      resetDialogState();
    }
  };

  const submitEditItem = async (itemId: string) => {
    if (!activeProject) {
      setFormError('Missing project context');
      return;
    }
    const parsed = parseJson(formValues.itemValue);
    if (!parsed.ok) {
      setFormError(parsed.message);
      return;
    }
    setFormLoading(true);
    setFormError(null);
    const { data, error } = await invokeFunction<{ item: Item }>('item-update', {
      body: { itemId, value: parsed.data },
      bearer: activeProject.active_key,
    });
    setFormLoading(false);
    if (error) {
      setFormError(error);
      return;
    }
    if (data?.item) {
      setItems((prev) => prev.map((item) => (item.id === itemId ? data.item : item)));
      resetDialogState();
    }
  };

  const handleGearClick = (target: 'project' | 'collection') => {
    if (target === 'project') {
      if (!activeProject) return;
      setStep(3);
      return;
    }
    if (!activeProject || !activeCollection) return;
    setStep(4);
  };

  const handleCopyEmail = useCallback(() => {
    const email = session.user?.email;
    if (!email) return;
    void navigator.clipboard.writeText(email).then(() => setCopyToast('Email copied'));
  }, [session.user?.email]);

  const handlePrimaryAction = () => {
    if (currentStep === 0) {
      setDialog({ type: 'create-project' });
    } else if (currentStep === 1) {
      if (!activeProject) return;
      setDialog({ type: 'create-collection' });
    } else if (currentStep === 2) {
      if (!activeCollection) return;
      setDialog({ type: 'add-item' });
    } else if (currentStep === 3 || currentStep === 4) {
      const snippet = activeProject ? buildItemSnippet(activeProject, activeCollection ?? collectionMeta) : '';
      if (!snippet) return;
      void navigator.clipboard.writeText(snippet).then(() => setCopyToast('Snippet copied'));
    } else if (currentStep === 5) {
      handleCopyEmail();
    }
  };

  const handleDeleteSelectedProjects = () => {
    if (!selectedProjects.size) return;
    setProjectsActionError(null);
    if (selectedProjects.size > 1) {
      setProjectsActionError('Delete projects one at a time.');
      return;
    }
    const iterator = selectedProjects.values().next();
    const projectId = iterator.value;
    if (!projectId) return;
    const project = projects.find((entry) => entry.id === projectId);
    if (!project) {
      setProjectsActionError('Selected project could not be found');
      return;
    }
    setDeleteInputs({ name: '', code: '' });
    setDeleteError(null);
    setDeleteDialog({ project, code: createVerificationCode() });
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialog(null);
    setDeleteInputs({ name: '', code: '' });
    setDeleteError(null);
  };

  const handleConfirmProjectDeletion = async () => {
    if (!deleteDialog) return;
    setDeleteLoading(true);
    setDeleteError(null);
    const { project } = deleteDialog;
    const { data, error } = await invokeFunction<{ archived: string[]; deleted: string[] }>('create-project', {
      body: { action: 'delete', projectIds: [project.id] },
    });
    setDeleteLoading(false);
    if (error) {
      setDeleteError(error);
      return;
    }
    clearProjectSelection();
    if (activeProjectId === project.id) {
      setActiveProjectId(null);
      setCollections([]);
      setActiveCollectionId(null);
      setCollectionMeta(null);
    }
    const deletedIds = data?.deleted ?? [];
    const archivedIds = data?.archived ?? [];
    if (deletedIds.includes(project.id)) {
      setCopyToast(`${project.name} deleted`);
    } else if (archivedIds.includes(project.id)) {
      setCopyToast(`${project.name} archived`);
    } else {
      setCopyToast(`${project.name} updated`);
    }
    closeDeleteDialog();
    await refresh();
  };

  const handleDeleteSelectedCollections = async () => {
    if (!activeProject || !selectedCollections.size) return;
    const confirmed =
      typeof window === 'undefined'
        ? true
        : window.confirm('Delete selected collections? Any items inside will also be removed.');
    if (!confirmed) return;
    setCollectionsError(null);
    setCollectionsActionError(null);
    const ids = Array.from(selectedCollections);
    const { error } = await invokeFunction('create-collection', {
      body: { action: 'delete', projectId: activeProject.id, collectionIds: ids },
      bearer: activeProject.active_key,
    });
    if (error) {
      setCollectionsActionError(error);
      return;
    }
    setCollections((prev) => prev.filter((collection) => !selectedCollections.has(collection.id)));
    clearCollectionSelection();
    if (activeCollectionId && ids.includes(activeCollectionId)) {
      setActiveCollectionId(null);
      setCollectionMeta(null);
    }
  };

  const handleDeleteSelectedItems = async () => {
    if (!selectedItems.size || !activeProject) return;
    const ids = Array.from(selectedItems);
    setItemsError(null);
    await Promise.all(
      ids.map(async (itemId) => {
        const { error } = await invokeFunction('item-delete', {
          body: { itemId },
          bearer: activeProject.active_key,
        });
        if (error) {
          setItemsError(error);
        }
      }),
    );
    setItems((prev) => prev.filter((item) => !selectedItems.has(item.id)));
    clearItemSelection();
  };

  const handleRotateKeys = async () => {
    if (!activeProject) return;
    setRotateMessage(null);
    const { data, error } = await invokeFunction<{ project: Project; message: string }>('rotate-key', {
      body: {},
      bearer: activeProject.active_key,
    });
    if (error) {
      setRotateMessage(error);
      return;
    }
    if (data?.project) {
      setProject(data.project);
      setRotateMessage(data.message ?? 'Keys rotated');
    }
  };

  const handleAccessToggle = async (
    field: 'allow_anonymous_read' | 'allow_anonymous_append' | 'allow_anonymous_delete',
    checked: boolean,
  ) => {
    if (!activeProject || !activeCollectionId || !collectionMeta) return;
    setAccessError(null);
    setAccessSaving(true);
    const body = {
      action: 'updateAccess' as const,
      projectId: activeProject.id,
      collectionId: activeCollectionId,
      allowAnonymousRead: field === 'allow_anonymous_read' ? checked : collectionMeta.allow_anonymous_read,
      allowAnonymousAppend: field === 'allow_anonymous_append' ? checked : collectionMeta.allow_anonymous_append,
      allowAnonymousDelete: field === 'allow_anonymous_delete' ? checked : collectionMeta.allow_anonymous_delete,
    };
    const { data, error } = await invokeFunction<{ collection: Collection }>('create-collection', {
      body,
      bearer: activeProject.active_key,
    });
    setAccessSaving(false);
    if (error) {
      setAccessError(error);
      return;
    }
    if (data?.collection) {
      setCollectionMeta(data.collection);
      setCollections((prev) => prev.map((col) => (col.id === data.collection.id ? data.collection : col)));
    }
  };

  const runItemSearch = async (nextValue?: string) => {
    if (!activeProject || !activeCollectionId) {
      return;
    }
    const raw = nextValue ?? searchState[2] ?? '';
    const query = raw.trim();
    if (!query) {
      await loadItems(activeProject, activeCollectionId);
      return;
    }
    await loadItems(activeProject, activeCollectionId, { query });
  };

  const renderDialog = () => {
    if (!dialog) return null;
    const close = () => resetDialogState();
    const isProject = dialog.type === 'create-project';
    const isCollection = dialog.type === 'create-collection';
    const isAddItem = dialog.type === 'add-item';
    const isEdit = dialog.type === 'edit-item';
    const icon = isProject ? 'folder' : isCollection ? 'collections_bookmark' : isAddItem ? 'inventory_2' : 'edit';
    const title = isProject
      ? 'Create Project'
      : isCollection
        ? `Create Collection${activeProject ? ` in ${activeProject.name}` : ''}`
        : isAddItem
          ? `Add Item${activeCollection ? ` to ${activeCollection.name}` : ''}`
          : `Edit ${dialog.item.key}`;
    const eyebrow = '';
    const description = '';
    const primaryText = isProject
      ? 'Create'
      : isCollection
        ? 'Create'
        : isAddItem
          ? 'Create'
          : 'Save';

    const handleSave = () => {
      if (isProject) {
        void submitCreateProject();
      } else if (isCollection) {
        void submitCreateCollection();
      } else if (isAddItem) {
        void submitAddItem();
      } else if (isEdit) {
        void submitEditItem(dialog.item.id);
      }
    };

    return (
      <div className="dialog-backdrop" role="dialog" aria-modal="true">
        <div className="dialog-panel">
          <div className="dialog-header">
            <div className="dialog-heading">
              <span className="dialog-icon material-symbols-rounded" aria-hidden="true">
                {icon}
              </span>
              <div className="dialog-title-group">
                {eyebrow ? <p className="dialog-eyebrow">{eyebrow}</p> : null}
                <h3>{title}</h3>
              </div>
            </div>
          </div>
          {description ? <p className="dialog-subtitle">{description}</p> : null}
          <div className="dialog-body">
            {isProject ? (
              <label className="dialog-field">
                <span>Project name</span>
                <input
                  value={formValues.projectName}
                  onChange={(event) => setFormValues((prev) => ({ ...prev, projectName: event.target.value }))}
                  placeholder="My Project"
                />
              </label>
            ) : null}
            {isCollection ? (
              <label className="dialog-field">
                <span>Collection name</span>
                <input
                  value={formValues.collectionName}
                  onChange={(event) => setFormValues((prev) => ({ ...prev, collectionName: event.target.value }))}
                  placeholder="your-collection"
                />
              </label>
            ) : null}
            {(isAddItem || isEdit) && (
              <>
                {isAddItem ? (
                  <label className="dialog-field">
                    <span>Item key</span>
                    <input
                      value={formValues.itemKey}
                      onChange={(event) => setFormValues((prev) => ({ ...prev, itemKey: event.target.value }))}
                    placeholder="item1"
                    />
                  </label>
                ) : null}
                <label className="dialog-field">
                  <span>JSON value</span>
                  <textarea
                    rows={4}
                    value={formValues.itemValue}
                    onChange={(event) => setFormValues((prev) => ({ ...prev, itemValue: event.target.value }))}
                  />
                </label>
              </>
            )}
            {formError ? <p className="error dialog-error">{formError}</p> : null}
          </div>
          <div className="dialog-footer">
            <div className="dialog-secondary-actions" aria-label={isEdit ? 'Item actions' : undefined}>
              {isEdit ? (
                <>
                  <button
                    type="button"
                    className="dialog-icon-button"
                    onClick={() => void handleDialogRenameItem(dialog.item)}
                    title="Rename key"
                  >
                    <span className="sr-only">Rename key</span>
                    <span className="material-symbols-rounded" aria-hidden="true">
                      drive_file_rename_outline
                    </span>
                  </button>
                  <button
                    type="button"
                    className="dialog-icon-button"
                    onClick={() => void handleDialogDuplicateItem(dialog.item)}
                    title="Duplicate item"
                  >
                    <span className="sr-only">Duplicate item</span>
                    <span className="material-symbols-rounded" aria-hidden="true">
                      content_copy
                    </span>
                  </button>
                  <button
                    type="button"
                    className="dialog-icon-button icon-danger"
                    onClick={() => void handleDialogDeleteItem(dialog.item)}
                    title="Delete item"
                  >
                    <span className="sr-only">Delete item</span>
                    <span className="material-symbols-rounded" aria-hidden="true">
                      delete
                    </span>
                  </button>
                </>
              ) : null}
            </div>
            <div className="dialog-primary-actions">
              <button type="button" className="ghost" onClick={close} disabled={formLoading}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={handleSave} disabled={formLoading}>
                {formLoading ? 'Working…' : primaryText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteDialog = () => {
    if (!deleteDialog) return null;
    const { project, code } = deleteDialog;
    const alreadyArchived = Boolean(project.archived_at);
    const nameMatches = deleteInputs.name.trim() === project.name;
    const codeMatches = deleteInputs.code.trim() === code;
    const canConfirm = nameMatches && codeMatches && !deleteLoading;
    const subtitle = alreadyArchived
      ? 'This project is archived. Deleting it again permanently removes every collection and item.'
      : 'Deleting a project is a two-step process. The first confirmation archives it, protecting data in case you change your mind.';

    return (
      <div className="dialog-backdrop" role="dialog" aria-modal="true">
        <div className="dialog-panel danger">
          <div className="dialog-header">
            <div className="dialog-heading">
              <span className="dialog-icon dialog-icon-danger material-symbols-rounded" aria-hidden="true">
                warning
              </span>
              <div className="dialog-title-group">
                <p className="dialog-eyebrow">Delete project</p>
                <h3>{alreadyArchived ? 'Permanently delete this project' : 'Archive this project'}</h3>
              </div>
            </div>
            <button type="button" className="dialog-close" onClick={closeDeleteDialog} disabled={deleteLoading} aria-label="Close dialog">
              <span className="material-symbols-rounded" aria-hidden="true">
                close
              </span>
            </button>
          </div>
          <p className="dialog-subtitle">{subtitle}</p>
          <div className="dialog-body">
            <div className="danger-callout">
              <p>
                Type <strong>{project.name}</strong> and enter the code <code>{code}</code> to continue.
              </p>
            </div>
            <label className="dialog-field">
              <span>Project name</span>
              <input
                value={deleteInputs.name}
                onChange={(event) => setDeleteInputs((prev) => ({ ...prev, name: event.target.value }))}
                placeholder={project.name}
                disabled={deleteLoading}
              />
            </label>
            <label className="dialog-field">
              <span>Security code</span>
              <input
                value={deleteInputs.code}
                onChange={(event) => setDeleteInputs((prev) => ({ ...prev, code: event.target.value }))}
                placeholder={code}
                disabled={deleteLoading}
                inputMode="numeric"
              />
            </label>
            {deleteError ? <p className="error dialog-error">{deleteError}</p> : null}
          </div>
          <div className="dialog-footer">
            <div />
            <div className="dialog-primary-actions">
              <button type="button" className="ghost" onClick={closeDeleteDialog} disabled={deleteLoading}>
                Cancel
              </button>
              <button
                type="button"
                className="primary danger"
                onClick={() => void handleConfirmProjectDeletion()}
                disabled={!canConfirm}
              >
                {deleteLoading ? 'Working…' : alreadyArchived ? 'Delete project' : 'Archive project'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const settingsSnippet = activeProject ? buildItemSnippet(activeProject, activeCollection ?? collectionMeta) : '';

  const searchStep = getSearchStep();
  const searchIsExpanded =
    searchStep !== null ? isMobileMenu || searchExpanded[searchStep] || Boolean(searchState[searchStep]?.trim()) : false;
  const searchDisabled =
    currentStep === 1
      ? !activeProject
      : currentStep === 2
        ? !activeCollection
        : currentStep >= 3
          ? true
          : false;
  const canClearSearch = searchStep !== null && Boolean(searchState[searchStep]);

  const primaryLabel =
    currentStep === 0
      ? 'Create project'
      : currentStep === 1
        ? 'Create collection'
        : currentStep === 2
          ? 'Add item'
          : currentStep === 5
            ? 'Copy email'
            : 'Copy snippet';
  const primaryDisabled =
    (currentStep === 1 && !activeProject) ||
    (currentStep === 2 && !activeCollection) ||
    (currentStep === 3 && !activeProject) ||
    (currentStep === 4 && (!activeProject || !activeCollection)) ||
    (currentStep === 5 && !session?.user?.email);
  const primaryIcon = currentStep >= 3 ? 'content_copy' : 'add_circle';
  const projectSelectionActive = selectedProjects.size > 0;
  const collectionSelectionActive = selectedCollections.size > 0;
  const itemSelectionActive = selectedItems.size > 0;
  const projectToolbarHasSearch = isMobileMenu && !projectSelectionActive;
  const collectionToolbarHasSearch = isMobileMenu && !collectionSelectionActive;
  const itemToolbarHasSearch = isMobileMenu && !itemSelectionActive;
  const previousWorkspaceStep = activeCollection ? 2 : activeProject ? 1 : 0;
  const projectSortLabel = projectSortOrder === 'asc' ? 'A→Z' : 'Z→A';
  const collectionSortLabel = collectionSortOrder === 'asc' ? 'A→Z' : 'Z→A';
  const projectSortTitle = `Sort projects by name (${projectSortLabel})`;
  const collectionSortTitle = `Sort collections by name (${collectionSortLabel})`;

  useEffect(() => {
    if (mobileToolbarMenuStep === 0 && projectSelectionActive) {
      setMobileToolbarMenuStep(null);
    } else if (mobileToolbarMenuStep === 1 && collectionSelectionActive) {
      setMobileToolbarMenuStep(null);
    } else if (mobileToolbarMenuStep === 2 && itemSelectionActive) {
      setMobileToolbarMenuStep(null);
    }
  }, [mobileToolbarMenuStep, projectSelectionActive, collectionSelectionActive, itemSelectionActive]);

  const renderSearchControl = () => {
    if (searchStep === null || currentStep >= 3) return null;
    const searchPillClassName = [
      'search-pill',
      searchIsExpanded ? 'expanded' : '',
      searchDisabled ? 'disabled' : '',
      searchIsExpanded && isMobileMenu ? 'mobile-expanded' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div className={searchPillClassName} role="search">
        {!searchIsExpanded ? (
          <button type="button" className="search-trigger" aria-label="Search" onClick={expandSearch} disabled={searchDisabled}>
            <span className="material-symbols-rounded" aria-hidden="true">
              search
            </span>
          </button>
        ) : (
          <span className="material-symbols-rounded search-icon" aria-hidden="true">
            search
          </span>
        )}
        {searchIsExpanded ? (
          <>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={currentStep === 0 ? 'Search projects' : currentStep === 1 ? 'Search collections' : 'Filter items'}
              value={currentStep < 3 ? searchState[currentStep as SearchStep] : ''}
              onChange={(event) => {
                const value = event.target.value;
                if (currentStep < 3) {
                  setSearchValueForStep(currentStep as SearchStep, value);
                }
                if (currentStep === 2 && !value.trim()) {
                  void runItemSearch('');
                }
              }}
              onFocus={() => {
                if (currentStep < 3) {
                  setSearchExpandedForStep(currentStep as SearchStep, true);
                }
              }}
              onBlur={() => {
                if (currentStep < 3) {
                  collapseSearchIfEmpty(currentStep as SearchStep);
                }
              }}
              onKeyDown={(event) => {
                if (currentStep === 2 && event.key === 'Enter') {
                  event.preventDefault();
                  const target = event.target as HTMLInputElement;
                  void runItemSearch(target.value);
                }
              }}
              disabled={searchDisabled}
            />
            <button type="button" className="search-clear" onClick={handleClearSearch} disabled={!canClearSearch} aria-label="Clear search">
              <span className="material-symbols-rounded" aria-hidden="true">
                close
              </span>
            </button>
          </>
        ) : null}
      </div>
    );
  };

  const flattenToolbarButtons = (nodes: React.ReactNode): ToolbarMenuButtonElement[] => {
    const flattened: ToolbarMenuButtonElement[] = [];
    React.Children.forEach(nodes, (child) => {
      if (!child) return;
      if (isNodeWithChildren(child) && child.type === React.Fragment) {
        flattened.push(...flattenToolbarButtons(child.props.children));
        return;
      }
      if (isToolbarMenuButtonElement(child)) {
        flattened.push(child);
      }
    });
    return flattened;
  };

  const getToolbarMenuLabel = (element: ToolbarMenuButtonElement): string | null => {
    const ariaLabel = typeof element.props['aria-label'] === 'string' ? element.props['aria-label'].toLowerCase() : '';
    if (ariaLabel.includes('refresh')) return 'Refresh';
    if (ariaLabel.includes('sort')) return 'Sort';
    return null;
  };

  const renderToolbarButtonsForStep = (step: SearchStep, buttons: React.ReactNode) => {
    if (!isMobileMenu || !isCompactMobileMenu) {
      return <div className="toolbar-icon-row">{buttons}</div>;
    }
    const buttonList = flattenToolbarButtons(buttons);
    const extractNodeText = (node: React.ReactNode): string => {
      if (node === null || node === undefined) return '';
      if (typeof node === 'string' || typeof node === 'number') return String(node);
      if (Array.isArray(node)) return node.map(extractNodeText).join(' ');
      if (React.isValidElement<{ children?: React.ReactNode }>(node)) return extractNodeText(node.props.children);
      return '';
    };
    const menuOpen = mobileToolbarMenuStep === step;
    return (
      <div
        className="toolbar-menu"
        ref={(node) => {
          toolbarMenuRefs.current[step] = node;
        }}
      >
        <button
          type="button"
          className="toolbar-menu-trigger"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Open toolbar actions"
          onClick={() => setMobileToolbarMenuStep(menuOpen ? null : step)}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            menu
          </span>
          <span className="sr-only">Toggle toolbar actions</span>
        </button>
        {menuOpen ? (
          <div className="toolbar-menu-popover" role="menu" onClick={() => setMobileToolbarMenuStep(null)}>
            {buttonList.map((child, index) => {
              if (!React.isValidElement(child)) {
                return null;
              }
              const labelText = getToolbarMenuLabel(child);
              const cleanedChildren = React.Children.toArray(child.props.children).filter(
                (node) => !(typeof node === 'string' && !node.trim()),
              );
              const labelChild = cleanedChildren.find(
                (node): node is React.ReactElement<{ className?: string; children?: React.ReactNode }> =>
                  React.isValidElement<{ className?: string; children?: React.ReactNode }>(node) &&
                  typeof node.props.className === 'string' &&
                  node.props.className.includes('toolbar-label'),
              );
              const ariaLabelRaw = typeof child.props['aria-label'] === 'string' ? child.props['aria-label'] : '';
              const baseLabelText = labelChild ? extractNodeText(labelChild.props.children) : '';
              const combinedLabelText = labelText
                ? [baseLabelText, labelText].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim()
                : baseLabelText || ariaLabelRaw || 'Action';
              const normalizedLabelText = combinedLabelText.toLowerCase().includes('settings')
                ? 'Settings'
                : combinedLabelText;
              const renderedLabelContent = <span className="toolbar-menu-label-text">{normalizedLabelText}</span>;

              const enhancedChild = React.cloneElement(child, {
                className: 'toolbar-menu-button',
                children: <span className="toolbar-menu-content">{renderedLabelContent}</span>,
              });
              return (
                <div key={`toolbar-menu-item-${step}-${index}`} className="toolbar-menu-item">
                  {enhancedChild}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderProjectToolbarButtons = () => (
    <>
      <button
        type="button"
        className="toolbar-icon labelled"
        aria-label={`${projectSortTitle}. Click to toggle order`}
        title={`${projectSortTitle}. Click to toggle order`}
        aria-pressed={projectSortOrder === 'desc'}
        onClick={toggleProjectSortOrder}
      >
        <span className="material-symbols-rounded" aria-hidden="true">
          sort_by_alpha
        </span>
        <span className="toolbar-label">{projectSortLabel}</span>
      </button>
      {projectSelectionActive ? (
        <>
          <button
            type="button"
            className="toolbar-icon danger"
            aria-label="Delete selected projects"
            title="Delete selected projects"
            onClick={() => {
              void handleDeleteSelectedProjects();
            }}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              delete
            </span>
          </button>
          <button type="button" className="toolbar-icon text" onClick={clearProjectSelection}>
            Deselect all
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="toolbar-icon"
            aria-label="Refresh projects"
            onClick={() => {
              void refresh();
            }}
            disabled={projectsLoading}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              refresh
            </span>
          </button>
          <button
            type="button"
            className="toolbar-icon accent labelled"
            aria-label={primaryLabel}
            title={primaryLabel}
            onClick={handlePrimaryAction}
            disabled={primaryDisabled}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              {primaryIcon}
            </span>
            <span className="toolbar-label">Create</span>
          </button>
        </>
      )}
    </>
  );

  const renderCollectionToolbarButtons = () => (
    <>
      <button
        type="button"
        className="toolbar-icon labelled"
        aria-label={`${collectionSortTitle}. Click to toggle order`}
        title={`${collectionSortTitle}. Click to toggle order`}
        aria-pressed={collectionSortOrder === 'desc'}
        onClick={toggleCollectionSortOrder}
      >
        <span className="material-symbols-rounded" aria-hidden="true">
          sort_by_alpha
        </span>
        <span className="toolbar-label">{collectionSortLabel}</span>
      </button>
      {collectionSelectionActive ? (
        <>
          <button
            type="button"
            className="toolbar-icon danger"
            aria-label="Delete selected collections"
            title="Delete selected collections"
            onClick={() => {
              void handleDeleteSelectedCollections();
            }}
            disabled={!selectedCollections.size}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              delete
            </span>
          </button>
          <button type="button" className="toolbar-icon text" onClick={clearCollectionSelection}>
            Deselect all
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="toolbar-icon"
            aria-label="Open project settings"
            title="Open project settings"
            onClick={() => handleGearClick('project')}
            disabled={!activeProject}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              settings
            </span>
          </button>
          <button
            type="button"
            className="toolbar-icon"
            aria-label="Refresh collections"
            onClick={() => {
              void loadCollections(activeProject!);
            }}
            disabled={collectionsLoading}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              refresh
            </span>
          </button>
          <button
            type="button"
            className="toolbar-icon accent labelled"
            aria-label={primaryLabel}
            title={primaryLabel}
            onClick={handlePrimaryAction}
            disabled={primaryDisabled}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              {primaryIcon}
            </span>
            <span className="toolbar-label">Create</span>
          </button>
        </>
      )}
    </>
  );

  const renderItemToolbarButtons = () => (
    <>
      {itemSelectionActive ? (
        <>
          <button
            type="button"
            className="toolbar-icon danger"
            aria-label="Delete selected items"
            title="Delete selected items"
            onClick={() => {
              void handleDeleteSelectedItems();
            }}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              delete
            </span>
          </button>
          <button type="button" className="toolbar-icon text" onClick={clearItemSelection}>
            Deselect all
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="toolbar-icon"
            aria-label="Open collection settings"
            title="Open collection settings"
            onClick={() => handleGearClick('collection')}
            disabled={!activeCollection}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              settings
            </span>
          </button>
          <button
            type="button"
            className="toolbar-icon"
            aria-label="Refresh items"
            onClick={() => {
              if (activeProject && activeCollection) {
                void loadItems(activeProject, activeCollection.id);
              }
            }}
            disabled={itemsLoading}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              refresh
            </span>
          </button>
          <button
            type="button"
            className="toolbar-icon accent labelled"
            aria-label={primaryLabel}
            title={primaryLabel}
            onClick={handlePrimaryAction}
            disabled={primaryDisabled}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              {primaryIcon}
            </span>
            <span className="toolbar-label">Create</span>
          </button>
        </>
      )}
    </>
  );

  const renderCloseToolbarButtons = () => (
    <button
      type="button"
      className="toolbar-icon"
      aria-label="Close"
      title="Close"
      onClick={() => setStep(previousWorkspaceStep)}
    >
      <span className="material-symbols-rounded" aria-hidden="true">
        close
      </span>
    </button>
  );

  return (
    <div className="workflow-card">
      <div className="card-header">
        <button type="button" className="card-title" onClick={handleTitleClick}>
          Shanework
        </button>
        <button type="button" className="user-pill" aria-label="Account settings" onClick={() => setStep(5)}>
          <span className="user-avatar">{userInitials}</span>
          <span className="user-name">{session.user.email ?? 'You'}</span>
          <span className={`user-role-badge role-${profile?.role ?? 'unknown'}`}>{userRoleLabel}</span>
        </button>
      </div>
      <div className="card">
        <div className="step-nav">
          <div className="breadcrumb">
            {breadcrumbTrail.map(({ label, step }, index) => {
              const isActive = step === currentStep;
              const isClickable = step < currentStep;
              const isLastCrumb = index === breadcrumbTrail.length - 1;
              return (
                <Fragment key={`${label}-${step}`}>
                  {index ? (
                    <span className="crumb-separator material-symbols-rounded" aria-hidden="true">
                      chevron_right
                    </span>
                  ) : null}
                  {menuLoading && isLastCrumb ? <MenuLoadingIndicator active /> : null}
                  <span
                    className={`crumb${isActive ? ' active' : ''}${isClickable ? ' clickable' : ''}`}
                    onClick={() => handleBreadcrumbClick(step)}
                  >
                    {label}
                  </span>
                </Fragment>
              );
            })}
          </div>
        </div>

        <div className="view-port">
          <div className="view-track">
            <section className="view" hidden={currentStep !== 0}>
              <div className="view-toolbar">
                <div className="toolbar-title">
                  <span className="toolbar-title-icon material-symbols-rounded" aria-hidden="true">
                    folder
                  </span>
                  <p className="card-subtitle">Your Projects</p>
                </div>
                <div className={`toolbar-actions${projectToolbarHasSearch ? ' has-search' : ''}`}>
                  {projectSelectionActive ? (
                    <>
                      <span className="toolbar-selection-label">{selectedProjects.size} selected</span>
                      <div className="toolbar-icon-row">{renderProjectToolbarButtons()}</div>
                    </>
                  ) : (
                    <>
                      {renderSearchControl()}
                      {renderToolbarButtonsForStep(0, renderProjectToolbarButtons())}
                    </>
                  )}
                </div>
              </div>
              {projectsError ? <p className="error">{projectsError}</p> : null}
              {projectsLoading ? <p className="muted loading-banner">Loading projects…</p> : null}
              {projectsActionError ? <p className="error">{projectsActionError}</p> : null}
              <div className="list">
                {filteredProjects.map((project) => {
                  const isActiveRow = currentStep > 0 && project.id === activeProjectId;
                  const isArchived = Boolean(project.archived_at);
                  const rowClasses = `list-row${isActiveRow ? ' active' : ''}${isArchived ? ' archived' : ''}`;
                  return (
                  <div key={project.id} className={rowClasses} onClick={() => handleProjectSelection(project.id)}>
                    <div className="row-info">
                      <input
                        type="checkbox"
                        checked={selectedProjects.has(project.id)}
                        onChange={(event) => {
                          event.stopPropagation();
                          toggleProjectSelection(project.id, event.target.checked);
                        }}
                        onClick={(event) => event.stopPropagation()}
                      />
                      <div>
                        <strong>{project.name}</strong>
                        <small>{project.id}</small>
                        {isArchived ? <small className="status-note">Archived {formatTimestamp(project.archived_at)}</small> : null}
                      </div>
                    </div>
                    <span className={`badge${isArchived ? ' badge-archived' : ''}`}>
                      {isArchived ? 'Archived' : project.created_at ? new Date(project.created_at).toLocaleDateString() : '—'}
                    </span>
                  </div>
                );
                })}
                {!projectsLoading && !filteredProjects.length ? (
                  <div className="empty-state">
                    <p>No results found</p>
                    <button
                      type="button"
                      className="toolbar-icon accent labelled"
                      onClick={handlePrimaryAction}
                      disabled={primaryDisabled}
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">
                        add_circle
                      </span>
                      <span className="toolbar-label">Create a project</span>
                    </button>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="view account-view" hidden={currentStep !== 5}>
              <div className="view-toolbar">
                <div className="toolbar-title">
                  <span className="toolbar-title-icon material-symbols-rounded" aria-hidden="true">
                    account_circle
                  </span>
                  <p className="card-subtitle">Account</p>
                </div>
                <div className="toolbar-actions">
                  <div className="toolbar-icon-row">{renderCloseToolbarButtons()}</div>
                </div>
              </div>
              <div className="account-grid">
                <div className="account-panel">
                  <div className="account-profile">
                    <span className="account-avatar">{userInitials}</span>
                    <div>
                      <h3>{accountEmail === '—' ? 'Account' : accountEmail}</h3>
                      <p>
                        Account ID · <code>{accountId}</code>
                      </p>
                    </div>
                  </div>
                  <dl className="account-meta">
                    <div>
                      <dt>Email</dt>
                      <dd>{accountEmail}</dd>
                    </div>
                    <div>
                      <dt>Created</dt>
                      <dd>{accountCreated}</dd>
                    </div>
                  <div>
                    <dt>Role</dt>
                    <dd>
                      <span className={`user-role-pill role-${profile?.role ?? 'unknown'}`}>{userRoleLabel}</span>
                    </dd>
                  </div>
                  </dl>
                  <div className="account-actions">
                    <button type="button" className="toolbar-icon text" onClick={handleCopyEmail}>
                      Copy email
                    </button>
                    <button type="button" className="toolbar-icon text">
                      Manage notifications
                    </button>
                  <button
                    type="button"
                    className="toolbar-icon text accent"
                    onClick={() => {
                      void handleLogout();
                    }}
                    disabled={logoutLoading}
                  >
                    {logoutLoading ? 'Signing out…' : 'Logout'}
                  </button>
                  {isAdmin ? (
                    <Link className="toolbar-icon text" to="/admin">
                      Open admin
                    </Link>
                  ) : null}
                  </div>
                </div>
                <div className="account-panel">
                  <h3>Security</h3>
                  <p className="muted">Reset your password or manage multi-factor authentication from Supabase Auth.</p>
                  <div className="account-actions">
                    <button type="button" className="toolbar-icon text">
                      Open Supabase Auth
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="view" hidden={currentStep !== 1}>
              {!activeProject ? (
                <p className="muted">Select a project.</p>
              ) : (
                <>
                  <div className="view-toolbar">
                    <div className="toolbar-title">
                      <span className="toolbar-title-icon material-symbols-rounded" aria-hidden="true">
                        collections_bookmark
                      </span>
                      <p className="card-subtitle">{activeProject.name}</p>
                    </div>
                    <div className={`toolbar-actions${collectionToolbarHasSearch ? ' has-search' : ''}`}>
                      {collectionSelectionActive ? (
                        <>
                          <span className="toolbar-selection-label">{selectedCollections.size} selected</span>
                          <div className="toolbar-icon-row">{renderCollectionToolbarButtons()}</div>
                        </>
                      ) : (
                        <>
                          {renderSearchControl()}
                          {renderToolbarButtonsForStep(1, renderCollectionToolbarButtons())}
                        </>
                      )}
                    </div>
                  </div>
                  {collectionsError ? <p className="error">{collectionsError}</p> : null}
                  {collectionsActionError ? <p className="error">{collectionsActionError}</p> : null}
                  <div className="list">
                    {filteredCollections.map((collection) => (
                      <div
                        key={collection.id}
                        className={`list-row${collection.id === activeCollectionId ? ' active' : ''}`}
                        onClick={() => handleCollectionSelection(collection.id)}
                      >
                        <div className="row-info">
                            <input
                              type="checkbox"
                              checked={selectedCollections.has(collection.id)}
                              onChange={(event) => {
                                event.stopPropagation();
                                toggleCollectionSelection(collection.id, event.target.checked);
                              }}
                              onClick={(event) => event.stopPropagation()}
                            />
                          <div>
                            <strong>{collection.name}</strong>
                            <small>{collection.id}</small>
                          </div>
                        </div>
                        <span className="badge">{new Date(collection.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                    {showCollectionsEmpty ? (
                      <div className="empty-state">
                        <p>No results found</p>
                        <button
                          type="button"
                          className="toolbar-icon accent labelled"
                          onClick={handlePrimaryAction}
                          disabled={primaryDisabled}
                        >
                          <span className="material-symbols-rounded" aria-hidden="true">
                            add_circle
                          </span>
                          <span className="toolbar-label">Create a collection</span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </section>

            <section className="view" hidden={currentStep !== 2}>
              {!activeCollection ? (
                <p className="muted">Pick a collection.</p>
              ) : (
                <>
                  <div className="view-toolbar">
                    <div className="toolbar-title">
                      <span className="toolbar-title-icon material-symbols-rounded" aria-hidden="true">
                        inventory_2
                      </span>
                      <p className="card-subtitle">{activeCollection.name}</p>
                    </div>
                    <div className={`toolbar-actions${itemToolbarHasSearch ? ' has-search' : ''}`}>
                      {itemSelectionActive ? (
                        <>
                          <span className="toolbar-selection-label">{selectedItems.size} selected</span>
                          <div className="toolbar-icon-row">{renderItemToolbarButtons()}</div>
                        </>
                      ) : (
                        <>
                          {renderSearchControl()}
                          {renderToolbarButtonsForStep(2, renderItemToolbarButtons())}
                        </>
                      )}
                    </div>
                  </div>
                    {itemsError ? <p className="error">{itemsError}</p> : null}
                    <div className="items-table-wrapper">
                      {filteredItems.length ? (
                        <table className="items-table">
                          <thead>
                            <tr>
                              <th>
                                <button
                                  type="button"
                                  className="table-sort"
                                  onClick={() => toggleItemSort('item')}
                                  aria-label={getItemSortAriaLabel('item')}
                                >
                                  <span>Item</span>
                                  <span className="material-symbols-rounded" aria-hidden="true">
                                    {getItemSortIcon('item')}
                                  </span>
                                </button>
                              </th>
                              <th>
                                <button
                                  type="button"
                                  className="table-sort"
                                  onClick={() => toggleItemSort('value')}
                                  aria-label={getItemSortAriaLabel('value')}
                                >
                                  <span>Value</span>
                                  <span className="material-symbols-rounded" aria-hidden="true">
                                    {getItemSortIcon('value')}
                                  </span>
                                </button>
                              </th>
                              <th>
                                <button
                                  type="button"
                                  className="table-sort"
                                  onClick={() => toggleItemSort('updated')}
                                  aria-label={getItemSortAriaLabel('updated')}
                                >
                                  <span>Updated</span>
                                  <span className="material-symbols-rounded" aria-hidden="true">
                                    {getItemSortIcon('updated')}
                                  </span>
                                </button>
                              </th>
                              <th>
                                <button
                                  type="button"
                                  className="table-sort"
                                  onClick={() => toggleItemSort('created')}
                                  aria-label={getItemSortAriaLabel('created')}
                                >
                                  <span>Created</span>
                                  <span className="material-symbols-rounded" aria-hidden="true">
                                    {getItemSortIcon('created')}
                                  </span>
                                </button>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredItems.map((item) => {
                              const updatedTimestamp = resolveItemUpdatedTimestamp(item);
                              const createdTimestamp = resolveItemCreatedTimestamp(item);
                              const formattedUpdated = formatTimestamp(updatedTimestamp);
                              const formattedCreated = formatTimestamp(createdTimestamp);
                              return (
                                <tr key={item.id} className="item-row">
                                  <td data-label={ITEM_SORT_LABELS.item}>
                                    <div className="row-info">
                                      <input
                                        type="checkbox"
                                        checked={selectedItems.has(item.id)}
                                        onChange={(event) => toggleItemSelection(item.id, event.target.checked)}
                                      />
                                      <button
                                        type="button"
                                        className="item-key"
                                        onClick={() => openEditItemDialog(item)}
                                      >
                                        {item.key}
                                      </button>
                                    </div>
                                  </td>
                                  <td data-label={ITEM_SORT_LABELS.value}>
                                    <button type="button" className="item-value" onClick={() => openEditItemDialog(item)}>
                                      <code>{JSON.stringify(item.value)}</code>
                                    </button>
                                  </td>
                                  <td data-label={ITEM_SORT_LABELS.updated}>{formattedUpdated}</td>
                                  <td data-label={ITEM_SORT_LABELS.created}>{formattedCreated}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : showItemsEmpty ? (
                        <div className="empty-state">
                          <p>No results found</p>
                          <button type="button" className="toolbar-icon accent labelled" onClick={handlePrimaryAction}>
                            <span className="material-symbols-rounded" aria-hidden="true">
                              add_circle
                            </span>
                            <span className="toolbar-label">Create your first item</span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                </>
              )}
            </section>

            <section className="view settings-view" hidden={currentStep !== 3}>
              <div className="view-toolbar">
                <div className="toolbar-title">
                  <span className="toolbar-title-icon material-symbols-rounded" aria-hidden="true">
                    tune
                  </span>
                  <p className="card-subtitle">{activeProject ? activeProject.name : 'Project settings'}</p>
                </div>
                <div className="toolbar-actions">
                  <div className="toolbar-icon-row">{renderCloseToolbarButtons()}</div>
                </div>
              </div>
              {!activeProject ? (
                <p className="muted">Select a project to open settings.</p>
              ) : (
                <div className="settings-grid">
                  <div className="settings-panel project-panel">
                    <div className="settings-header">
                      <h3>{activeProject.name}</h3>
                      <button type="button" className="ghost" onClick={() => void handleRotateKeys()}>
                        Rotate keys
                      </button>
                    </div>
                    {rotateMessage ? <p className="hint">{rotateMessage}</p> : null}
                    <ProjectKeys project={activeProject} />
                  </div>
                </div>
              )}
            </section>

            <section className="view settings-view" hidden={currentStep !== 4}>
              <div className="view-toolbar">
                <div className="toolbar-title">
                  <span className="toolbar-title-icon material-symbols-rounded" aria-hidden="true">
                    tune
                  </span>
                  <p className="card-subtitle">{(collectionMeta ?? activeCollection)?.name ?? 'Collection settings'}</p>
                </div>
                <div className="toolbar-actions">
                  <div className="toolbar-icon-row">{renderCloseToolbarButtons()}</div>
                </div>
              </div>
              {!activeCollection ? (
                <p className="muted">Pick a collection to open settings.</p>
              ) : (
                <div className="settings-grid">
                  <div className="settings-panel anon-panel">
                    <div className="settings-header">
                      <div>
                        <p className="eyebrow">Anonymous access</p>
                      </div>
                    </div>
                    <p className="muted anon-panel-description">
                      Choose what anonymous visitors can read, append, or delete in this collection. Enabling these
                      options lets API calls succeed without bearer authentication.
                    </p>
                    {accessError ? <p className="error">{accessError}</p> : null}
                    <div className="anon-grid">
                      {accessFields.map((field) => {
                        const labelMap: Record<(typeof accessFields)[number], string> = {
                          allow_anonymous_read: 'Anonymous read',
                          allow_anonymous_append: 'Anonymous append',
                          allow_anonymous_delete: 'Anonymous delete',
                        };
                        const current = (collectionMeta ?? activeCollection)?.[field];
                        return (
                          <label key={field} className="anon-toggle">
                            <input
                              type="checkbox"
                              checked={Boolean(current)}
                              onChange={(event) => {
                                void handleAccessToggle(field, event.target.checked);
                              }}
                              disabled={accessSaving || !activeCollectionId}
                            />
                            <span>{labelMap[field]}</span>
                          </label>
                        );
                      })}
                    </div>
                    {accessSaving ? <span className="hint">Saving…</span> : null}
                  </div>

                  {activeProject ? (
                    <div className="settings-panel">
                      <CodeSnippet title="Client example (cURL)" code={settingsSnippet || buildItemSnippet(activeProject)} />
                      <CodeSnippet title="Client example (JS)" code={buildListSnippet(activeProject, activeCollection ?? collectionMeta)} />
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
      {copyToast ? <div className="toast">{copyToast}</div> : null}
      {renderDialog()}
      {renderDeleteDialog()}
    </div>
  );
};

export default WorkspacePage;
